import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsAPI } from '../../services/api';
import { Save, X } from 'lucide-react';
import Toast from '../../components/Toast';
import ImageUpload from '../../components/ImageUpload';

const EventForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        fullDescription: '',
        date: '',
        locationName: '',
        locationAddress: '',
        locationCity: '',
        locationState: '',
        locationZipCode: '',
        image: '/logo.png',
        maxAttendees: 100,
        currentAttendees: 0,
        registrationRequired: true,
        registrationUrl: '',
        category: 'workshop',
        featured: false,
    });

    useEffect(() => {
        if (isEdit) {
            fetchEvent();
        }
    }, [id]);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            const response = await eventsAPI.getById(id);
            // Handle both old format (response.data.data) and new format (response.data)
            const event = response.data?.id ? response.data : response.data?.data;
            setFormData({
                title: event.title,
                slug: event.slug,
                description: event.description,
                fullDescription: event.fullDescription,
                date: new Date(event.date).toISOString().slice(0, 16),
                locationName: event.location?.name || '',
                locationAddress: event.location?.address || '',
                locationCity: event.location?.city || '',
                locationState: event.location?.state || '',
                locationZipCode: event.location?.zipCode || '',
                image: event.image,
                maxAttendees: event.maxAttendees,
                currentAttendees: event.currentAttendees,
                registrationRequired: event.registrationRequired,
                registrationUrl: event.registrationUrl || '',
                category: event.category,
                featured: event.featured,
            });
        } catch (error) {
            console.error('Error fetching event:', error);
            showToast('Failed to load event', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: parseInt(value) || 0,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const eventData = {
                title: formData.title,
                slug: formData.slug,
                description: formData.description,
                fullDescription: formData.fullDescription,
                date: new Date(formData.date).toISOString(),
                location: {
                    name: formData.locationName,
                    address: formData.locationAddress,
                    city: formData.locationCity,
                    state: formData.locationState,
                    zipCode: formData.locationZipCode,
                },
                image: formData.image,
                maxAttendees: formData.maxAttendees,
                currentAttendees: formData.currentAttendees,
                registrationRequired: formData.registrationRequired,
                registrationUrl: formData.registrationUrl,
                category: formData.category,
                featured: formData.featured,
            };

            if (isEdit) {
                await eventsAPI.update(id, eventData);
                showToast('Event updated successfully', 'success');
            } else {
                await eventsAPI.create(eventData);
                showToast('Event created successfully', 'success');
            }
            setTimeout(() => navigate('/admin/events'), 1500);
        } catch (error) {
            console.error('Error saving event:', error);
            showToast(error.response?.data?.message || 'Failed to save event', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type) => {
        setToast({ message, type });
    };

    const categories = ['workshop', 'seminar', 'field-day', 'conference', 'training'];

    if (loading && isEdit) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEdit ? 'Edit Event' : 'Add New Event'}
                </h1>
                <p className="text-gray-600 mt-1">
                    {isEdit ? 'Update event information' : 'Create a new event'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Slug *
                            </label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category *
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            >
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Short Description *
                            </label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Description *
                            </label>
                            <textarea
                                name="fullDescription"
                                value={formData.fullDescription}
                                onChange={handleChange}
                                required
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date & Time *
                            </label>
                            <input
                                type="datetime-local"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <ImageUpload
                                value={formData.image}
                                onChange={(url) => setFormData({ ...formData, image: url })}
                                label="Event Image"
                            />
                        </div>
                    </div>
                </div>

                {/* Location */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Location</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Name
                            </label>
                            <input
                                type="text"
                                name="locationName"
                                value={formData.locationName}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            <input
                                type="text"
                                name="locationAddress"
                                value={formData.locationAddress}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                City
                            </label>
                            <input
                                type="text"
                                name="locationCity"
                                value={formData.locationCity}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                State
                            </label>
                            <input
                                type="text"
                                name="locationState"
                                value={formData.locationState}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Zip Code
                            </label>
                            <input
                                type="text"
                                name="locationZipCode"
                                value={formData.locationZipCode}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* Registration */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Registration</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Max Attendees
                            </label>
                            <input
                                type="number"
                                name="maxAttendees"
                                value={formData.maxAttendees}
                                onChange={handleNumberChange}
                                min="0"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Current Attendees
                            </label>
                            <input
                                type="number"
                                name="currentAttendees"
                                value={formData.currentAttendees}
                                onChange={handleNumberChange}
                                min="0"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Registration URL
                            </label>
                            <input
                                type="url"
                                name="registrationUrl"
                                value={formData.registrationUrl}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="registrationRequired"
                                    checked={formData.registrationRequired}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-earth-green focus:ring-earth-green border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700">
                                    Registration Required
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="featured"
                                    checked={formData.featured}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-earth-green focus:ring-earth-green border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700">
                                    Featured Event
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/events')}
                        className="btn-outline inline-flex items-center space-x-2"
                    >
                        <X className="h-5 w-5" />
                        <span>Cancel</span>
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                    >
                        <Save className="h-5 w-5" />
                        <span>{loading ? 'Saving...' : isEdit ? 'Update Event' : 'Create Event'}</span>
                    </button>
                </div>
            </form>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default EventForm;
